import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { ColumnDef } from '@tanstack/react-table';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { ElementType, ReactNode } from 'react';
import { Avatar } from '../../../../../design-system/avatar/avatar/avatar';
import { ColumnHeaderWithSorting } from '../../../../../design-system/data-table/column-header-with-sorting';
import { BaseTableRow } from '../../../../../design-system/data-table/data-table';
import { PayrollRecordView } from '../../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../../utils/get-amount-with-currency';
import { getBeneficiaryName } from '../../../../payments/utils/get-beneficiary-name';
import { isVopPayeeCheck } from '../../../../payments/utils/is-vop-payee-check';
import { EmployeeNameCell } from './employee-column-cell';
import { TooltipConfig } from './payee-check-badge-wrapper';

export const NAME_COLUMN_ID = 'name';

export type PayrollSummaryTableRow = PayrollRecordView & BaseTableRow;

export const payrollSummaryTableColumnDefinition: ColumnDef<PayrollSummaryTableRow>[] = [
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original.employee;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 6,
    minSize: 6,
  },
  {
    id: NAME_COLUMN_ID,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Employee" />,
    accessorFn: (row) => getBeneficiaryName({ firstName: row.employee.firstName, lastName: row.employee.lastName }),
    cell: ({ row, table }) => {
      const employeeName = getBeneficiaryName({
        firstName: row.original.employee.firstName,
        lastName: row.original.employee.lastName,
      });
      const latestPayeeCheckBadge = isVopPayeeCheck(row.original.employee.latestPayeeCheck)
        ? getLatestPayeeCheckBadge(row.original.employee.latestPayeeCheck?.code, () =>
            table.options.meta?.onRowPrimaryAction?.(row.original.id),
          )
        : {
            isVisible: false as const,
          };

      return <EmployeeNameCell employeeName={employeeName} latestPayeeCheckBadge={latestPayeeCheckBadge} />;
    },
    sortingFn: 'alphanumeric',
    size: 25,
  },
  {
    id: 'iban',
    header: ({ column, table }) => {
      // Only show IBAN header if at least one row has an IBAN
      const hasIban = table.getRowModel().rows.some((row) => row.original.employee.destination?.iban);
      if (!hasIban) return null;
      return <ColumnHeaderWithSorting column={column} label="IBAN" />;
    },
    cell: ({ row }) => {
      const destination = row.original.employee.destination;
      return destination?.iban ? <span>{destination.iban}</span> : null;
    },
    accessorFn: (row) => row.employee.destination?.iban,
    size: 22,
    minSize: 22,
  },
  {
    id: 'accountNumber',
    header: ({ column, table }) => {
      // Only show Account Number header if at least one row does NOT have an IBAN
      const hasAccount = table
        .getRowModel()
        .rows.some(
          (row) => !row.original.employee.destination?.iban && row.original.employee.destination?.accountNumber,
        );
      if (!hasAccount) return null;
      return <ColumnHeaderWithSorting column={column} label="Account Number" />;
    },
    cell: ({ row }) => {
      const destination = row.original.employee.destination;
      return !destination?.iban && destination?.accountNumber ? <span>{destination.accountNumber}</span> : null;
    },
    accessorFn: (row) => (!row.employee.destination?.iban ? row.employee.destination?.accountNumber : undefined),
    size: 16,
    minSize: 16,
  },
  {
    id: 'sortCode',
    header: ({ column, table }) => {
      // Only show Sort Code header if at least one row does NOT have an IBAN
      const hasSortCode = table
        .getRowModel()
        .rows.some((row) => !row.original.employee.destination?.iban && row.original.employee.destination?.sortCode);
      if (!hasSortCode) return null;
      return <ColumnHeaderWithSorting column={column} label="Sort Code" />;
    },
    cell: ({ row }) => {
      const destination = row.original.employee.destination;
      return !destination?.iban && destination?.sortCode ? <span>{destination.sortCode}</span> : null;
    },
    accessorFn: (row) => (!row.employee.destination?.iban ? row.employee.destination?.sortCode : undefined),
    size: 10,
    minSize: 10,
  },
  {
    id: 'reference',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Reference" />,
    cell: ({ row }) => {
      const record = row.original;
      // Use payroll reference if available, otherwise use a default
      const reference = record.payroll?.reference || 'Payroll';
      return <span>{reference}</span>;
    },
    accessorFn: (row) => row.payroll?.reference || 'Payroll',
    size: 13,
    minSize: 13,
  },
  {
    id: 'netPay',
    header: ({ column }) => (
      <div className="flex justify-end">
        <ColumnHeaderWithSorting column={column} label="Net Pay" />
      </div>
    ),
    cell: ({ row }) => {
      const record = row.original;
      const formattedNetPay = getAmountWithCurrency({
        amount: record.netPay,
        currency: record.employee.destination?.currency,
        isAccountBalance: true,
      });
      return <p className="text-right">{formattedNetPay}</p>;
    },
    accessorKey: 'netPay',
    size: 13,
    minSize: 13,
  },
];

const PAYEE_CHECK_BADGE_COLORS = {
  SUCCESS: {
    hex: '#169B62',
    textClassName: 'text-seapoint-green-badge',
  },
  WARNING: {
    hex: '#FF771D',
    textClassName: 'text-seapoint-orange',
  },
  ERROR: {
    hex: '#FF531D',
    textClassName: 'text-seapoint-red',
  },
};

type EditPayrollRecordConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      onEditPayrollRecord: () => void;
    };

export type LatestPayeeCheckBadgeConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      icon: ReactNode;
      text?: string;
      textClassName?: string;
      tooltipConfig: TooltipConfig;
      editPayrollRecordConfig: EditPayrollRecordConfig;
    };

const getLatestPayeeCheckBadge = (
  latestPayeeCheck: VerifyPayeeCheckResponseCode | undefined,
  onEditPayrollRecord: () => void,
): LatestPayeeCheckBadgeConfig => {
  if (!latestPayeeCheck) {
    return {
      isVisible: false,
    };
  }

  switch (latestPayeeCheck) {
    case VerifyPayeeCheckResponseCode.MATCHED: {
      const severity = 'SUCCESS';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleCheck, PAYEE_CHECK_BADGE_COLORS[severity].hex, 'Details fully match'),
        tooltipConfig: {
          isVisible: true,
          text: 'Details fully match',
        },
        editPayrollRecordConfig: {
          isVisible: false,
        },
      };
    }
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH: {
      const severity = 'WARNING';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        tooltipConfig: {
          isVisible: true,
          text: "Some details don't match",
        },
        editPayrollRecordConfig: {
          isVisible: true,
          onEditPayrollRecord,
        },
      };
    }
    case VerifyPayeeCheckResponseCode.NO_MATCH:
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR: {
      const severity = 'ERROR';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        tooltipConfig: {
          isVisible: true,
          text: "Some details don't match",
        },
        editPayrollRecordConfig: {
          isVisible: true,
          onEditPayrollRecord,
        },
      };
    }
    default:
      return exhaustiveCheck(latestPayeeCheck);
  }
};

const createBadgeIcon = (Icon: ElementType, color: string, label?: string) => (
  <Icon color={color} strokeWidth={1} aria-label={label} className="h-full w-full" />
);
